/**
 * Orchestration for a lookup run: the per-address pipeline, the worker pool that
 * moves several addresses through it at once, and the run metadata that ends up
 * on the front of the report.
 */

import { lookupMailchimp, MAILCHIMP_CONCURRENCY } from "./mailchimp"
import { findBookings, findClientAccounts, findCorrespondence, findDirectBookings, findEnquiries, findGiftVouchers, findQuotes } from "./firestore-lookup"

/**
 * The checks each address goes through, in order. The page renders one chip per
 * stage so it is always visible where a given address has got to.
 */
export const STAGES = [
	{ key: "mailchimp", label: "Mailchimp" },
	{ key: "accounts", label: "Accounts" },
	{ key: "enquiries", label: "Enquiries" },
	{ key: "bookings", label: "Bookings" },
	{ key: "quotes", label: "Quotes" },
	{ key: "extras", label: "Vouchers" },
	{ key: "correspondence", label: "Emails" },
]

const emptyResult = (entry) => ({
	...entry,
	status: "RUNNING",
	error: "",
	mailchimp: null,
	mailchimpSkipped: true,
	clients: [],
	enquiries: [],
	bookings: [],
	quotes: [],
	directBookings: [],
	giftVouchers: [],
	correspondence: { messages: [], accessible: true },
	totals: {
		lifetimeValue: 0,
		liveLifetimeValue: 0,
		paidByClient: 0,
		paymentsRecorded: 0,
	},
})

/**
 * Run every check for one address. Stage failures are recorded against the
 * address rather than thrown, so one bad record cannot abandon the run - the
 * report then shows exactly which address is incomplete and why.
 */
export const processAddress = async (entry, context, onStage) => {
	const { apiKey, audiences, archivedIndex, resolveCampaign, staff, clientIndex } = context
	const result = emptyResult(entry)

	const stage = (key, state, detail) => {
		if (onStage) onStage(key, state, detail)
	}

	// Mailchimp
	if (apiKey) {
		result.mailchimpSkipped = false
		stage("mailchimp", "running")

		try {
			result.mailchimp = await lookupMailchimp(apiKey, entry.email, { audiences, archivedIndex, resolveCampaign })
			stage("mailchimp", "done", `${result.mailchimp.memberships.length} list(s)`)
		} catch (error) {
			result.mailchimp = { memberships: [], activity: [], related: [], error: error.message }
			stage("mailchimp", "failed", error.message)
		}
	} else {
		stage("mailchimp", "skipped", "no key")
	}

	try {
		// Client accounts - everything below hangs off these
		stage("accounts", "running")
		result.clients = await findClientAccounts(entry.email, clientIndex)
		stage("accounts", "done", `${result.clients.length} account(s)`)

		const clientIDs = result.clients.map((client) => client.id)

		stage("enquiries", "running")
		result.enquiries = await findEnquiries(clientIDs, staff)
		stage("enquiries", "done", `${result.enquiries.length}`)

		stage("bookings", "running")
		result.bookings = await findBookings(clientIDs, staff)
		stage("bookings", "done", `${result.bookings.length}`)

		stage("quotes", "running")
		result.quotes = await findQuotes(clientIDs)
		stage("quotes", "done", `${result.quotes.length}`)

		stage("extras", "running")
		const [directBookings, giftVouchers] = await Promise.all([findDirectBookings(clientIDs), findGiftVouchers(entry.email)])
		result.directBookings = directBookings
		result.giftVouchers = giftVouchers
		stage("extras", "done", `${directBookings.length + giftVouchers.length}`)

		stage("correspondence", "running")
		result.correspondence = await findCorrespondence(clientIDs, entry.email)
		stage("correspondence", result.correspondence.accessible ? "done" : "skipped", result.correspondence.accessible ? `${result.correspondence.messages.length}` : "no access")

		result.totals = {
			lifetimeValue: result.bookings.reduce((total, booking) => total + booking.customerPrice, 0),
			liveLifetimeValue: result.bookings.filter((booking) => booking.state === "Live").reduce((total, booking) => total + booking.customerPrice, 0),
			paidByClient: result.bookings.reduce((total, booking) => total + booking.paidByClient, 0),
			paymentsRecorded: result.bookings.reduce((total, booking) => total + booking.paymentsRecorded, 0),
		}

		result.status = "COMPLETE"
	} catch (error) {
		result.status = "ERROR"
		result.error = error.message || String(error)
	}

	return result
}

/**
 * Move `entries` through `worker` a few at a time, preserving input order in the
 * results. Concurrency is set by the Mailchimp connection limit, which is the
 * tightest constraint in the pipeline.
 */
export const runPool = async (entries, worker, concurrency = MAILCHIMP_CONCURRENCY) => {
	const results = new Array(entries.length)
	let cursor = 0

	const runNext = async () => {
		while (cursor < entries.length) {
			const index = cursor
			cursor += 1
			results[index] = await worker(entries[index], index)
		}
	}

	await Promise.all(Array.from({ length: Math.min(concurrency, entries.length) }, runNext))

	return results
}

/**
 * The provenance block on the front of the report: what was searched, how, and
 * what the run cannot speak to. Written out in full because the value of the
 * document depends on its limits being visible rather than implied.
 */
export const buildRunInfo = ({ caseReference, operator, sourceFile, sourceRows, generatedAt, audiences, mailchimpAccount, apiKeyUsed, archivedScanned, clientIndexScanned, correspondenceAccessible, skippedRows, sheetsScanned, addressCount }) => {
	const audienceNames = audiences.map((audience) => audience.name)

	const sourcesSearched = [
		apiKeyUsed
			? `Mailchimp account ${mailchimpAccount || "(name unavailable)"}: every audience on the account (${audienceNames.length}) searched by exact email address, plus each contact's membership record, tag history and the campaign activity Mailchimp holds against them.`
			: "Mailchimp was not searched on this run, because no API key was supplied.",
		archivedScanned ? "Mailchimp archived contacts were indexed audience by audience and folded into the results, because archived contacts are excluded from Mailchimp's member search." : "Mailchimp archived contacts were not indexed on this run, so an address archived in Mailchimp may be reported as not present.",
		clientIndexScanned ? `Booking system client accounts: the full clients collection was read (${clientIndexScanned.toLocaleString("en-GB")} records) and matched on email without regard to capitalisation.` : "Booking system client accounts: matched by exact query on the likely capitalisations of each address rather than a full scan.",
		"Enquiries, including deleted enquiries held in enquiries_removed, matched through every client account found for the address.",
		"Bookings, including removed bookings, together with the payments recorded against each one.",
		"Quotes: internal quote records and the published online quotes behind any client-facing quote link.",
		"Online bookings taken directly on the website, and gift vouchers bought with the address.",
		correspondenceAccessible ? "Email correspondence held in the monitored-mailbox archive, matched by client account and by sender address." : "Email correspondence was not searched: the archive is not readable by the account that produced this report.",
	]

	const limitations = [
		"Marketing data is reported as Mailchimp holds it today. Contacts deleted from Mailchimp, as opposed to archived or unsubscribed, leave no record that can be retrieved.",
		"Mailchimp returns at most the 50 most recent events per contact per audience, so campaign activity older than that may not appear for a heavily emailed address.",
		"Where an address appears in more than one client account, every account is reported and all figures are combined across them.",
		"Lifetime value is the sum of the total holiday cost recorded on every booking held against the address, including bookings later removed. That figure is the customer price stored on the booking (margins.customer_price). Where a booking carries no customer price the figure quoted on the originating enquiry is used instead, and the Cost field column on each booking row names which of the two every figure came from.",
		"Paid by client is the figure stored on the booking; payments recorded is the sum of individual payment records. The two can differ where payments were reconciled outside the system.",
		"Outbound email in the correspondence archive can only be matched where the message was linked to a client account, because recipient addresses are not stored in a queryable form.",
		skippedRows > 0 ? `${skippedRows} row(s) in the source file did not contain a recognisable email address and were not looked up.` : null,
		`${addressCount} distinct address(es) were looked up from ${sheetsScanned.map((sheet) => sheet.sheetName).join(", ") || "the source file"}.`,
	].filter(Boolean)

	return {
		caseReference,
		operator,
		sourceFile,
		sourceRows,
		generatedAt,
		audienceNames,
		mailchimpAccount,
		sourcesSearched,
		limitations,
		method: [
			{ label: "Case reference", value: caseReference },
			{ label: "Generated", value: generatedAt },
			{ label: "Date format", value: "Every date in this workbook is ISO 8601 in UTC. The PDF report shows the same dates in UK format." },
			{ label: "Prepared by", value: operator },
			{ label: "Source file", value: sourceFile },
			{ label: "Rows read from source", value: String(sourceRows) },
			{ label: "Addresses looked up", value: String(addressCount) },
			{ label: "Rows skipped (not an email address)", value: String(skippedRows) },
			{ label: "Mailchimp account", value: mailchimpAccount || "Not connected" },
			{ label: "Mailchimp audiences searched", value: audienceNames.join(", ") || "None" },
			{ label: "Mailchimp archived contacts indexed", value: archivedScanned ? "Yes" : "No" },
			{ label: "Client collection fully scanned", value: clientIndexScanned ? `Yes (${clientIndexScanned} records)` : "No" },
			{ label: "Correspondence archive readable", value: correspondenceAccessible ? "Yes" : "No" },
			...sourcesSearched.map((entry, index) => ({ label: `Source ${index + 1}`, value: entry })),
			...limitations.map((entry, index) => ({ label: `Limitation ${index + 1}`, value: entry })),
		],
	}
}
